import React, { useState, useEffect } from "react";
import AuthService from "services/auth.service";
import { API_URL_IMAGE } from "services/config";
import defaultImage from "../assets/img/default-avatar.png";
import SITE from "../services/siteConfig";
import styles from "./Settings.module.css";

export default function Settings() {
  const currentUser = AuthService.getCurrentUser();
  const [activeTab, setActiveTab] = useState("Profile");
  const [file, setFile] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash === "about") setActiveTab("About");
  }, []);

  const imageDir = currentUser?.imageURL;
  let profileImage = `${API_URL_IMAGE}${imageDir}`;
  if (profileImage.endsWith("none")) profileImage = defaultImage;

  /* ---- Profile Picture ---- */
  const isValidFileType = (f) =>
    ["image/svg+xml", "image/png", "image/jpeg", "image/gif"].includes(f.type);

  const handleProfilePictureUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!isValidFileType(f)) { alert("Invalid file type (SVG, PNG, JPG, GIF only)"); return; }
    if (f.size > 2 * 1024 * 1024) { alert("File too large (max 2MB)"); return; }

    const fd = new FormData();
    fd.append("file", f);
    fd.append("userId", currentUser.id);

    try {
      setUploading(true);
      const res = await AuthService.updateProfilePicture(fd);
      if (res?.imageUrl) {
        AuthService.updateCurrentUser({ ...currentUser, imageURL: res.imageUrl });
        setFile(f);
        alert("Profile picture updated!");
      }
    } catch { alert("Upload failed"); }
    finally { setUploading(false); }
  };

  /* ---- ID Upload ---- */
  const handleIDUpload = async (e, side) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { alert("File too large (max 5MB)"); return; }

    const fd = new FormData();
    fd.append("file", f);
    fd.append("side", side);

    try {
      setUploading(true);
      const res = await AuthService.uploadID(currentUser.id, fd);
      if (res?.imageUrl) {
        const key = `id${side.charAt(0).toUpperCase() + side.slice(1)}URL`;
        AuthService.updateCurrentUser({ ...currentUser, [key]: res.imageUrl, verificationStatus: "pending" });
        setFile(f);
      }
    } catch { alert("Upload failed"); }
    finally { setUploading(false); }
  };

  const handleSubmitVerification = async () => {
    if (!currentUser.idFrontURL || currentUser.idFrontURL.endsWith("none") ||
        !currentUser.idBackURL || currentUser.idBackURL.endsWith("none")) {
      alert("Please upload both front and back of your ID");
      return;
    }
    try {
      await AuthService.submitVerification(currentUser.id);
      AuthService.updateCurrentUser({ ...currentUser, verificationStatus: "pending" });
      alert("ID submitted for verification!");
    } catch { alert("Submission failed"); }
  };

  /* ---- Password Change ---- */
  const handlePasswordChange = async () => {
    if (!oldPass || !newPass || !confirmPass) { alert("All fields required"); return; }
    if (newPass !== confirmPass) { alert("Passwords don't match"); return; }
    try {
      await AuthService.updatePassword(currentUser.id, { password: newPass });
      alert("Password changed successfully!");
      setOldPass(""); setNewPass(""); setConfirmPass("");
    } catch { alert("Failed to change password"); }
  };

  /* ---- Tabs ---- */
  const tabs = ["Profile", "Verification", "ChangePassword", "About"];
  const current = AuthService.getCurrentUser() || currentUser; // refresh

  return (
    <div className="content mid-content">
      <div className={styles.welcome}>
        <h2 className={styles.welcomeTitle}>Settings</h2>
        <p className={styles.welcomeSub}>Manage your profile, security, and account settings</p>
      </div>

      {/* Tab nav */}
      <div className={styles.tabs}>
        {tabs.map(t => (
          <button
            key={t}
            className={`${styles.tab} ${activeTab === t ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(t)}
          >
            {t === "ChangePassword" ? "Security" : t}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className={styles.card}>
        <div className={styles.cardBody}>
          {/* Profile Tab */}
          {activeTab === "Profile" && (
            <>
              <div className={styles.profileHeader}>
                <div className={styles.avatarWrapper}>
                  <img src={profileImage} alt="Profile" className={styles.avatar} />
                  <label className={styles.avatarOverlay}>
                    <input type="file" accept="image/*" onChange={handleProfilePictureUpload} hidden disabled={uploading} />
                    {uploading ? "⏳" : "📷"}
                  </label>
                </div>
                <div>
                  <h3>{current.firstName} {current.lastName}</h3>
                  <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>{current.email}</p>
                </div>
              </div>

              <div className={styles.fieldGrid}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>First Name</label>
                  <input className={styles.fieldInput} value={current.firstName} disabled />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Last Name</label>
                  <input className={styles.fieldInput} value={current.lastName} disabled />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Email</label>
                  <input className={styles.fieldInput} value={current.email} disabled />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Phone</label>
                  <input className={styles.fieldInput} value={current.mobileNumber} disabled />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Date of Birth</label>
                  <input className={styles.fieldInput} value={current.dateOfBirth} disabled />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Gender</label>
                  <input className={styles.fieldInput} value={current.gender} disabled />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Address</label>
                  <input className={styles.fieldInput} value={current.address} />
                </div>
              </div>

              <div style={{ marginTop: "1rem", display: "flex", gap: "0.75rem" }}>
                <label className={styles.btnPrimary} style={{ cursor: "pointer" }}>
                  <input type="file" accept="image/*" onChange={handleProfilePictureUpload} hidden disabled={uploading} />
                  Update Photo
                </label>
                <button className={styles.btnOutline}>Save Changes</button>
              </div>
            </>
          )}

          {/* Verification Tab */}
          {activeTab === "Verification" && (
            <>
              <div className={styles.uploadGrid}>
                {/* Front */}
                <div className={styles.uploadBox}>
                  <div className={styles.uploadLabel}>ID Card Front</div>
                  <label className={styles.uploadArea}>
                    <input type="file" accept="image/*,.pdf" onChange={e => handleIDUpload(e, "front")} hidden disabled={uploading} />
                    {current.idFrontURL && !current.idFrontURL.endsWith("none") ? (
                      <>
                        <img src={`${API_URL_IMAGE}${current.idFrontURL}`} alt="ID Front" className={styles.idPreview} />
                        <span className={styles.uploadReplace}>Replace Front</span>
                      </>
                    ) : (
                      <div className={styles.uploadPlaceholder}>📄<span>Upload Front</span></div>
                    )}
                  </label>
                </div>
                {/* Back */}
                <div className={styles.uploadBox}>
                  <div className={styles.uploadLabel}>ID Card Back</div>
                  <label className={styles.uploadArea}>
                    <input type="file" accept="image/*,.pdf" onChange={e => handleIDUpload(e, "back")} hidden disabled={uploading} />
                    {current.idBackURL && !current.idBackURL.endsWith("none") ? (
                      <>
                        <img src={`${API_URL_IMAGE}${current.idBackURL}`} alt="ID Back" className={styles.idPreview} />
                        <span className={styles.uploadReplace}>Replace Back</span>
                      </>
                    ) : (
                      <div className={styles.uploadPlaceholder}>📄<span>Upload Back</span></div>
                    )}
                  </label>
                </div>
              </div>

              <div className={styles.verificationStatus}>
                <span style={{ color: "var(--text-secondary)" }}>Status: </span>
                <span className={`${styles.statusChip} ${current.verificationStatus === "verified" ? styles.chipSuccess : current.verificationStatus === "rejected" ? styles.chipDanger : styles.chipWarning}`}>
                  {(current.verificationStatus || "pending").toUpperCase()}
                </span>
              </div>

              <button
                className={styles.btnPrimary}
                onClick={handleSubmitVerification}
                disabled={!current.idFrontURL || current.idFrontURL.endsWith("none") || !current.idBackURL || current.idBackURL.endsWith("none")}
                style={{ opacity: (!current.idFrontURL || current.idFrontURL.endsWith("none") || !current.idBackURL || current.idBackURL.endsWith("none")) ? 0.5 : 1 }}
              >
                Submit ID for Verification
              </button>
            </>
          )}

          {/* Change Password Tab */}
          {activeTab === "ChangePassword" && (
            <div className={styles.passwordForm}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Current Password</label>
                <div className={styles.passWrapper}>
                  <input className={styles.fieldInput} type={showPass ? "text" : "password"} value={oldPass} onChange={e => setOldPass(e.target.value)} placeholder="Enter current password" />
                  <button className={styles.passToggle} onClick={() => setShowPass(!showPass)}>{showPass ? "🙈" : "👁️"}</button>
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>New Password</label>
                <input className={styles.fieldInput} type={showPass ? "text" : "password"} value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Enter new password" />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Confirm Password</label>
                <input className={styles.fieldInput} type={showPass ? "text" : "password"} value={confirmPass} onChange={e => setConfirmPass(e.target.value)} placeholder="Confirm new password" />
              </div>
              <button className={styles.btnPrimary} onClick={handlePasswordChange}>Change Password</button>
            </div>
          )}

          {/* About Tab */}
          {activeTab === "About" && (
            <div className={styles.about}>
              <h3>About {SITE.orgShort}</h3>
              <p>The <strong>{SITE.orgName} ({SITE.orgShort})</strong> is a globally recognized organization dedicated to advancing the massage and body profession. We set industry standards, certify qualified practitioners, and promote excellence in therapeutic care.</p>
              <h4>Our Mission</h4>
              <p>Elevate professional standards in massage therapy. Protect client safety through rigorous certification. Advocate for the integration of bodywork into holistic healthcare. Foster continuous education and ethical practice.</p>
              <h4>Certification Requirements</h4>
              <p>Minimum 500 hours of accredited training, passing score on competency exams, background checks and liability insurance, commitment to ethical guidelines.</p>
              <h4>Contact</h4>
              <p>Email: {SITE.email} | Phone: {SITE.phone} | Office Hours: {SITE.supportHours}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}